import { type SanityFrameBuilderType } from '@frame-builder/data/sanity/queries/types'
import { clamp } from '@frame-builder/lib/helpers'
import { getSanityImageUrl } from '@frame-builder/lib/image'
import {
  type FrameBuilderConfigurationWithLimits,
  type FrameBuilderState,
} from '@frame-builder/lib/types'

type FrameType = 'frame' | 'passepartout'

export interface FrameSettings {
  type: FrameType
  frameDiffuseUrl?: string
  frameTopSize: number
  frameRightSize: number
  frameBottomSize: number
  frameLeftSize: number
  frameDepth: number
  frameInset: number
  passepartoutColor?: string
  passepartoutCoreColor?: string
  passepartoutTopSize: number
  passepartoutRightSize: number
  passepartoutBottomSize: number
  passepartoutLeftSize: number
  passepartoutDepth: number
  artworkUrl?: string
  artworkWidth: number
  artworkHeight: number
  backgroundColor?: string
  generateBackFace?: boolean
}

/**
 * Determines if size is valid and returns it, otherwise returns default size.
 */
const getSize = (
  size: number | undefined,
  defaultSize: number,
  allowZero = false,
) => {
  // If size is undefined or zero and (zeros aren't allowed or size is not zero)
  if (!size && (!allowZero || size !== 0)) {
    return defaultSize
  }

  return size
}

/**
 * Gets frame settings from state.
 */
export const getFrameSettings = (
  frameBuilderType: SanityFrameBuilderType,
  frameBuilderConfiguration: FrameBuilderConfigurationWithLimits,
  state: FrameBuilderState,
  defaultState: FrameBuilderState,
) => {
  let artworkWidth = getSize(
    state.artwork.width,
    defaultState.artwork?.width ?? 0,
  )
  let artworkHeight = getSize(
    state.artwork.height,
    defaultState.artwork?.height ?? 0,
  )
  artworkWidth = clamp(
    artworkWidth,
    frameBuilderConfiguration.limits.minimumSize,
    Math.max(
      frameBuilderConfiguration.limits.maximumWidth,
      frameBuilderConfiguration.limits.maximumHeight,
    ),
  )
  artworkHeight = clamp(
    artworkHeight,
    frameBuilderConfiguration.limits.minimumSize,
    Math.max(
      frameBuilderConfiguration.limits.maximumWidth,
      frameBuilderConfiguration.limits.maximumHeight,
    ),
  )

  const frameSize = getSize(
    state.profile?.width,
    defaultState.profile?.width ?? 0,
  )
  const profileThickness = getSize(
    state.profile?.thickness,
    defaultState.profile?.thickness ?? 0,
  )
  const profileRabbetDepth = getSize(
    state.profile?.rabbetDepth,
    defaultState.profile?.rabbetDepth ?? 0,
  )
  const profileEdgeThickness =
    profileRabbetDepth > 0
      ? profileThickness - profileRabbetDepth
      : profileThickness / 4

  const artworkUrl =
    state.artwork.image?.url ?? defaultState.artwork?.image?.url
  const texture = state.texture?.image ?? defaultState.texture?.image

  const frameSettings: FrameSettings = {
    type: frameBuilderType === 'frame' ? 'frame' : 'passepartout',
    frameDiffuseUrl: getSanityImageUrl(texture) ?? '',
    frameTopSize: frameSize / 1000,
    frameRightSize: frameSize / 1000,
    frameBottomSize: frameSize / 1000,
    frameLeftSize: frameSize / 1000,
    frameDepth: profileThickness / 1000,
    frameInset: profileEdgeThickness / 1000,
    passepartoutColor: 'white',
    passepartoutTopSize: 0,
    passepartoutRightSize: 0,
    passepartoutBottomSize: 0,
    passepartoutLeftSize: 0,
    passepartoutDepth: 0,
    artworkUrl,
    artworkWidth: artworkWidth / 100,
    artworkHeight: artworkHeight / 100,
  }

  if (state.passepartout.color) {
    const passepartoutWidth = getSize(state.passepartout.width, 0)
    const passepartoutHeight = getSize(state.passepartout.height, 0)
    const passepartoutThickness = getSize(
      state.passepartout.color?.thickness,
      0,
    )

    // Add mount details
    frameSettings.passepartoutColor = state.passepartout.color.color.hex
    frameSettings.passepartoutCoreColor =
      state.passepartout.color.coreColor?.hex
    frameSettings.passepartoutTopSize = passepartoutHeight / 100
    frameSettings.passepartoutRightSize = passepartoutWidth / 100
    frameSettings.passepartoutBottomSize = passepartoutHeight / 100
    frameSettings.passepartoutLeftSize = passepartoutWidth / 100
    frameSettings.passepartoutDepth = passepartoutThickness / 1000
  }

  return frameSettings
}
